import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, DollarSign, Clock, Trash2, FileText, ClipboardList, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PackagesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [planningForms, setPlanningForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  const [showAssignFormsDialog, setShowAssignFormsDialog] = useState(false);
  const [selectedPackageForForms, setSelectedPackageForForms] = useState(null);
  const [selectedForms, setSelectedForms] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    base_price: '',
    features: [],
    duration_hours: '',
    category: 'Standard',
    image_url: '',
    assigned_forms: []
  });
  
  const [newAddon, setNewAddon] = useState({
    name: '',
    description: '',
    price: '',
    category: 'General',
    image_url: '',
    assigned_forms: [],
    available_for_packages: []
  });
  
  // Add-on form assignment state
  const [showAddonFormsDialog, setShowAddonFormsDialog] = useState(false);
  const [selectedAddonForForms, setSelectedAddonForForms] = useState(null);
  const [selectedAddonForms, setSelectedAddonForms] = useState([]);
  
  const [featureInput, setFeatureInput] = useState('');
  const [packageImagePreview, setPackageImagePreview] = useState('');
  const [addonImagePreview, setAddonImagePreview] = useState('');

  useEffect(() => {
    fetchPackages();
    fetchAddons();
    fetchPlanningForms();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddons = async () => {
    try {
      const response = await axios.get(`${API}/addons`);
      setAddons(response.data);
    } catch (error) {
      console.error('Failed to fetch add-ons:', error);
    }
  };

  const fetchPlanningForms = async () => {
    try {
      const response = await axios.get(`${API}/planning-forms`);
      setPlanningForms(response.data);
    } catch (error) {
      console.error('Failed to fetch planning forms:', error);
    }
  };

  const openAssignFormsDialog = (pkg) => {
    setSelectedPackageForForms(pkg);
    setSelectedForms(pkg.assigned_forms || []);
    setShowAssignFormsDialog(true);
  };

  const toggleFormSelection = (formId) => {
    setSelectedForms(prev => 
      prev.includes(formId) 
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };

  const handleSaveAssignedForms = async () => {
    if (!selectedPackageForForms) return;
    try {
      await axios.put(`${API}/packages/${selectedPackageForForms.id}`, {
        ...selectedPackageForForms,
        assigned_forms: selectedForms
      });
      toast.success('Forms assigned successfully');
      setShowAssignFormsDialog(false);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to assign forms');
    }
  };

  const getFormNames = (formIds) => {
    if (!formIds || formIds.length === 0) return 'None';
    const names = formIds.map(id => {
      const form = planningForms.find(f => f.id === id);
      return form ? form.name : null;
    }).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'None';
  };
  
  // Add-on form assignment functions
  const openAddonAssignFormsDialog = (addon) => {
    setSelectedAddonForForms(addon);
    setSelectedAddonForms(addon.assigned_forms || []);
    setShowAddonFormsDialog(true);
  };
  
  const toggleAddonFormSelection = (formId) => {
    setSelectedAddonForms(prev => 
      prev.includes(formId) 
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };
  
  const handleSaveAddonForms = async () => {
    if (!selectedAddonForForms) return;
    try {
      await axios.put(`${API}/addons/${selectedAddonForForms.id}`, {
        ...selectedAddonForForms,
        assigned_forms: selectedAddonForms
      });
      toast.success('Forms assigned to add-on successfully');
      setShowAddonFormsDialog(false);
      fetchAddons();
    } catch (error) {
      toast.error('Failed to assign forms to add-on');
    }
  };

  const handlePackageImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewPackage({ ...newPackage, image_url: base64String });
        setPackageImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddonImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewAddon({ ...newAddon, image_url: base64String });
        setAddonImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.name || !newPackage.base_price) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingPackage) {
        await axios.put(`${API}/packages/${editingPackage.id}`, newPackage);
        toast.success('Package updated successfully');
      } else {
        await axios.post(`${API}/packages`, newPackage);
        toast.success('Package created successfully');
      }
      setShowPackageDialog(false);
      setEditingPackage(null);
      setNewPackage({
        name: '',
        description: '',
        base_price: '',
        features: [],
        duration_hours: '',
        category: 'Standard',
        image_url: ''
      });
      setPackageImagePreview('');
      fetchPackages();
    } catch (error) {
      toast.error(editingPackage ? 'Failed to update package' : 'Failed to create package');
    }
  };

  const handleCreateAddon = async () => {
    if (!newAddon.name || !newAddon.price) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingAddon) {
        await axios.put(`${API}/addons/${editingAddon.id}`, newAddon);
        toast.success('Add-on updated successfully');
      } else {
        await axios.post(`${API}/addons`, newAddon);
        toast.success('Add-on created successfully');
      }
      setShowAddonDialog(false);
      setEditingAddon(null);
      setNewAddon({
        name: '',
        description: '',
        price: '',
        category: 'General',
        image_url: '',
        available_for_packages: []
      });
      setAddonImagePreview('');
      fetchAddons();
    } catch (error) {
      toast.error(editingAddon ? 'Failed to update add-on' : 'Failed to create add-on');
    }
  };

  const openEditPackageDialog = (pkg) => {
    setEditingPackage(pkg);
    setNewPackage({
      name: pkg.name,
      description: pkg.description || '',
      base_price: pkg.base_price,
      features: pkg.features || [],
      duration_hours: pkg.duration_hours || '',
      category: pkg.category || 'Standard',
      image_url: pkg.image_url || '',
      assigned_forms: pkg.assigned_forms || []
    });
    setPackageImagePreview(pkg.image_url || '');
    setShowPackageDialog(true);
  };

  const openEditAddonDialog = (addon) => {
    setEditingAddon(addon);
    setNewAddon({
      name: addon.name,
      description: addon.description || '',
      price: addon.price,
      category: addon.category || 'General',
      image_url: addon.image_url || '',
      available_for_packages: addon.available_for_packages || []
    });
    setAddonImagePreview(addon.image_url || '');
    setShowAddonDialog(true);
  };

  const resetPackageDialog = () => {
    setEditingPackage(null);
    setNewPackage({
      name: '',
      description: '',
      base_price: '',
      features: [],
      duration_hours: '',
      category: 'Standard',
      image_url: ''
    });
    setPackageImagePreview('');
  };

  const resetAddonDialog = () => {
    setEditingAddon(null);
    setNewAddon({
      name: '',
      description: '',
      price: '',
      category: 'General',
      image_url: '',
      available_for_packages: []
    });
    setAddonImagePreview('');
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      await axios.delete(`${API}/packages/${packageId}`);
      toast.success('Package deleted');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) return;

    try {
      await axios.delete(`${API}/addons/${addonId}`);
      toast.success('Add-on deleted');
      fetchAddons();
    } catch (error) {
      toast.error('Failed to delete add-on');
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setNewPackage({
        ...newPackage,
        features: [...newPackage.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setNewPackage({
      ...newPackage,
      features: newPackage.features.filter((_, i) => i !== index)
    });
  };

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="p-4 sm:p-6" data-testid="packages-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-4xl font-bold mb-2" data-testid="packages-heading">Packages & Pricing</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Define your service packages and add-ons</p>
        </div>

        <Tabs defaultValue="packages" className="space-y-6">
          <TabsList className="bg-muted/30 border-border">
            <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
            <TabsTrigger value="addons" data-testid="tab-addons">Add-Ons</TabsTrigger>
          </TabsList>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">Create service packages with pricing</p>
              <Dialog open={showPackageDialog} onOpenChange={(open) => { setShowPackageDialog(open); if (!open) resetPackageDialog(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-package-btn" onClick={resetPackageDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Package
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-package-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-foreground">
                        {editingPackage ? 'Edit Package' : 'Create New Package'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Package Name *</Label>
                          <Input
                            value={newPackage.name}
                            onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                            placeholder="e.g., Premium DJ Package"
                            className="bg-background border-border text-foreground"
                            data-testid="package-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Category</Label>
                          <Select value={newPackage.category} onValueChange={(v) => setNewPackage({ ...newPackage, category: v })}>
                            <SelectTrigger className="bg-background border-border text-foreground" data-testid="package-category-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="Standard">Standard</SelectItem>
                              <SelectItem value="Premium">Premium</SelectItem>
                              <SelectItem value="Deluxe">Deluxe</SelectItem>
                              <SelectItem value="Custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Package Image</Label>
                        <div className="flex items-center gap-4">
                          {packageImagePreview && (
                            <div className="w-24 h-24 rounded-lg overflow-hidden border border-border">
                              <img src={packageImagePreview} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handlePackageImageUpload}
                            className="bg-background border-border"
                            data-testid="package-image-input"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Max size: 2MB. Recommended: 800x600px</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Description</Label>
                        <textarea
                          value={newPackage.description}
                          onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                          className="w-full min-h-[80px] bg-background border border-border rounded-lg p-3 text-foreground"
                          placeholder="Brief description of the package"
                          data-testid="package-description-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Base Price ($) *</Label>
                          <Input
                            type="number"
                            value={newPackage.base_price}
                            onChange={(e) => setNewPackage({ ...newPackage, base_price: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="package-price-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Duration (hours)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={newPackage.duration_hours}
                            onChange={(e) => setNewPackage({ ...newPackage, duration_hours: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="package-duration-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Features Included</Label>
                        <div className="flex gap-2">
                          <Input
                            value={featureInput}
                            onChange={(e) => setFeatureInput(e.target.value)}
                            placeholder="e.g., Professional Sound System"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                            className="bg-background border-border text-foreground"
                            data-testid="package-feature-input"
                          />
                          <Button onClick={addFeature} type="button" data-testid="add-feature-btn">
                            Add
                          </Button>
                        </div>
                        <div className="space-y-2 mt-2">
                          {newPackage.features.map((feature, index) => (
                            <div key={index} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-foreground" data-testid={`feature-item-${index}`}>
                              <span>{feature}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFeature(index)}
                                className="text-red-500 hover:text-red-400"
                                data-testid={`remove-feature-btn-${index}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button
                        onClick={handleCreatePackage}
                        className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black"
                        data-testid="package-create-submit-btn"
                      >
                        {editingPackage ? 'Update Package' : 'Create Package'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="text-center py-12">Loading...</div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-packages-message">
                  <p className="text-muted-foreground">No packages yet. Create your first package!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-list">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="glass-card-hover rounded-2xl overflow-hidden relative"
                      data-testid={`package-item-${pkg.id}`}
                    >
                      {pkg.image_url && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={pkg.image_url} 
                            alt={pkg.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="absolute top-4 right-4 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditPackageDialog(pkg)}
                            className="text-muted-foreground hover:text-foreground bg-black/50 backdrop-blur"
                            data-testid={`edit-package-btn-${pkg.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletePackage(pkg.id)}
                            className="text-red-500 hover:text-red-400 bg-black/50 backdrop-blur"
                            data-testid={`delete-package-btn-${pkg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mb-4">
                          <span className="text-xs text-neon-blue font-semibold uppercase">{pkg.category}</span>
                          <h3 className="font-heading text-2xl font-bold mt-1">{pkg.name}</h3>
                          {pkg.description && (
                            <p className="text-sm text-muted-foreground mt-2">{pkg.description}</p>
                          )}
                        </div>
                        <div className="flex items-baseline gap-1 mb-4">
                          <span className="text-4xl font-bold text-neon-blue">${pkg.base_price}</span>
                          {pkg.duration_hours && (
                            <span className="text-muted-foreground">/ {pkg.duration_hours}hrs</span>
                          )}
                        </div>
                        {pkg.features && pkg.features.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {pkg.features.map((feature, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <div className="w-1.5 h-1.5 rounded-full bg-neon-blue"></div>
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Assigned Forms Section */}
                        <div className="pt-4 border-t border-border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <ClipboardList className="w-4 h-4" />
                              <span>Forms: {pkg.assigned_forms?.length || 0}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openAssignFormsDialog(pkg)}
                              className="border-border hover:bg-muted/50"
                              data-testid={`assign-forms-btn-${pkg.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Assign Forms
                            </Button>
                          </div>
                          {pkg.assigned_forms?.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              {getFormNames(pkg.assigned_forms)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Add-Ons Tab */}
            <TabsContent value="addons">
              <div className="flex justify-between items-center mb-6">
                <p className="text-muted-foreground">Manage additional services and upgrades</p>
                <Dialog open={showAddonDialog} onOpenChange={(open) => { setShowAddonDialog(open); if (!open) resetAddonDialog(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-neon-purple hover:bg-neon-purple/80 text-white" data-testid="create-addon-btn" onClick={resetAddonDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Add-On
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border border-border max-w-xl" data-testid="create-addon-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-foreground">
                        {editingAddon ? 'Edit Add-On' : 'Create New Add-On'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-foreground">Add-On Name *</Label>
                        <Input
                          value={newAddon.name}
                          onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                          placeholder="e.g., Fog Machine"
                          className="bg-background border-border text-foreground"
                          data-testid="addon-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Add-On Image</Label>
                        <div className="flex items-center gap-4">
                          {addonImagePreview && (
                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-border">
                              <img src={addonImagePreview} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleAddonImageUpload}
                            className="bg-background border-border"
                            data-testid="addon-image-input"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Max size: 2MB</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Description</Label>
                        <Input
                          value={newAddon.description}
                          onChange={(e) => setNewAddon({ ...newAddon, description: e.target.value })}
                          placeholder="Brief description"
                          className="bg-background border-border text-foreground"
                          data-testid="addon-description-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Price ($) *</Label>
                          <Input
                            type="number"
                            value={newAddon.price}
                            onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="addon-price-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Category</Label>
                          <Select value={newAddon.category} onValueChange={(v) => setNewAddon({ ...newAddon, category: v })}>
                            <SelectTrigger className="bg-background border-border text-foreground" data-testid="addon-category-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="General">General</SelectItem>
                              <SelectItem value="Equipment">Equipment</SelectItem>
                              <SelectItem value="Lighting">Lighting</SelectItem>
                              <SelectItem value="Effects">Effects</SelectItem>
                              <SelectItem value="Staff">Staff</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Package Availability */}
                      <div className="space-y-2">
                        <Label className="text-foreground">Available For Packages</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Leave all unchecked to make this add-on available for all packages
                        </p>
                        <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-background/50 rounded-lg border border-border">
                          {packages.length === 0 ? (
                            <p className="text-xs text-muted-foreground col-span-2">No packages created yet</p>
                          ) : (
                            packages.map((pkg) => (
                              <div key={pkg.id} className="flex items-center gap-2">
                                <Checkbox
                                  id={`addon-pkg-${pkg.id}`}
                                  checked={newAddon.available_for_packages?.includes(pkg.id) || false}
                                  onCheckedChange={(checked) => {
                                    const current = newAddon.available_for_packages || [];
                                    if (checked) {
                                      setNewAddon({ ...newAddon, available_for_packages: [...current, pkg.id] });
                                    } else {
                                      setNewAddon({ ...newAddon, available_for_packages: current.filter(id => id !== pkg.id) });
                                    }
                                  }}
                                  data-testid={`addon-package-checkbox-${pkg.id}`}
                                />
                                <label htmlFor={`addon-pkg-${pkg.id}`} className="text-sm text-foreground cursor-pointer">
                                  {pkg.name}
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleCreateAddon}
                        className="w-full bg-neon-purple hover:bg-neon-purple/80 text-white"
                        data-testid="addon-create-submit-btn"
                      >
                        {editingAddon ? 'Update Add-On' : 'Create Add-On'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {addons.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-addons-message">
                  <p className="text-muted-foreground">No add-ons yet. Create your first add-on!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="addons-list">
                  {addons.map((addon) => (
                    <div
                      key={addon.id}
                      className="glass-card-hover rounded-xl overflow-hidden relative"
                      data-testid={`addon-item-${addon.id}`}
                    >
                      {addon.image_url && (
                        <div className="h-32 overflow-hidden">
                          <img 
                            src={addon.image_url} 
                            alt={addon.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditAddonDialog(addon)}
                            className="text-muted-foreground hover:text-foreground bg-black/50 backdrop-blur"
                            data-testid={`edit-addon-btn-${addon.id}`}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAddonAssignFormsDialog(addon)}
                            className="text-muted-foreground hover:text-foreground bg-black/50 backdrop-blur"
                            data-testid={`addon-assign-forms-btn-${addon.id}`}
                          >
                            <ClipboardList className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAddon(addon.id)}
                            className="text-red-500 hover:text-red-400 bg-black/50 backdrop-blur"
                            data-testid={`delete-addon-btn-${addon.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="text-xs text-neon-purple font-semibold uppercase mb-1">{addon.category}</div>
                        <h3 className="font-semibold mb-1">{addon.name}</h3>
                        {addon.description && (
                          <p className="text-xs text-muted-foreground mb-2">{addon.description}</p>
                        )}
                        <div className="text-xl font-bold text-neon-purple">+${addon.price}</div>
                        {addon.available_for_packages?.length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="text-neon-blue">
                              {addon.available_for_packages.length === 1 ? '1 package' : `${addon.available_for_packages.length} packages`}
                            </span>
                          </div>
                        )}
                        {(!addon.available_for_packages || addon.available_for_packages.length === 0) && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="text-green-500">All packages</span>
                          </div>
                        )}
                        {addon.assigned_forms?.length > 0 && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            <FileText className="w-3 h-3 inline mr-1" />
                            {addon.assigned_forms.length} form(s)
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

      {/* Assign Forms Dialog */}
      <Dialog open={showAssignFormsDialog} onOpenChange={setShowAssignFormsDialog}>
        <DialogContent className="bg-card border border-border max-w-md" data-testid="assign-forms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
              <ClipboardList className="w-5 h-5 text-primary" />
              Assign Forms
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select forms that clients with the "{selectedPackageForForms?.name}" package should fill out.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
            {planningForms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No planning forms available.</p>
                <p className="text-sm">Create forms in the Forms section first.</p>
              </div>
            ) : (
              planningForms.map((form) => (
                <div
                  key={form.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedForms.includes(form.id)
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => toggleFormSelection(form.id)}
                  data-testid={`form-checkbox-${form.id}`}
                >
                  <Checkbox
                    checked={selectedForms.includes(form.id)}
                    onCheckedChange={() => toggleFormSelection(form.id)}
                    className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{form.name}</div>
                    {form.description && (
                      <div className="text-xs text-muted-foreground">{form.description}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {form.fields?.length || 0} field(s)
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAssignFormsDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignedForms}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
              data-testid="save-assigned-forms-btn"
            >
              Save ({selectedForms.length} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add-on Assign Forms Dialog */}
      <Dialog open={showAddonFormsDialog} onOpenChange={setShowAddonFormsDialog}>
        <DialogContent className="bg-card border border-border max-w-md" data-testid="addon-assign-forms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
              <ClipboardList className="w-5 h-5 text-neon-purple" />
              Assign Forms to Add-on
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select forms that clients with the "{selectedAddonForForms?.name}" add-on should fill out.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
            {planningForms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No planning forms available.</p>
                <p className="text-sm">Create forms in the Forms section first.</p>
              </div>
            ) : (
              planningForms.map((form) => (
                <div
                  key={form.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedAddonForms.includes(form.id)
                      ? 'border-neon-purple bg-neon-purple/10'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => toggleAddonFormSelection(form.id)}
                  data-testid={`addon-form-checkbox-${form.id}`}
                >
                  <Checkbox
                    checked={selectedAddonForms.includes(form.id)}
                    onCheckedChange={() => toggleAddonFormSelection(form.id)}
                    className="border-border data-[state=checked]:bg-neon-purple data-[state=checked]:border-neon-purple"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{form.name}</div>
                    {form.description && (
                      <div className="text-xs text-muted-foreground">{form.description}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {form.fields?.length || 0} field(s)
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAddonFormsDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddonForms}
              className="bg-neon-purple hover:bg-neon-purple/80 text-white"
              data-testid="save-addon-forms-btn"
            >
              Save ({selectedAddonForms.length} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
